import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getEpisode,
  getJobLogs,
  listAvatars,
  getVideoJobsByAudioJob,
  createVideoJob,
  regenerateEpisode,
} from '../api';
import { useClientId } from '../hooks/useClient';
import AudioPlayer from '../components/AudioPlayer';
import VideoPlayer from '../components/VideoPlayer';
import StatusBadge from '../components/StatusBadge';
import type { Episode, JobLog, Avatar, VideoJob } from '../types/db';

const SCRIPT_MAX_CHARS = 2000;

const VIDEO_STATUS_LABEL: Record<VideoJob['status'], string> = {
  pending: 'In attesa',
  generating: 'Generazione',
  ready: 'Pronto',
  failed: 'Errore',
};

const VIDEO_STATUS_CLASS: Record<VideoJob['status'], string> = {
  pending: 'bg-slate-100 text-slate-700',
  generating: 'bg-amber-100 text-amber-700',
  ready: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
};

export default function JobDetail() {
  const clientId = useClientId();
  const { id } = useParams<{ id: string }>();

  const [episode, setEpisode] = useState<Episode | null>(null);
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [videoJobs, setVideoJobs] = useState<VideoJob[]>([]);
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  const [pollMsg, setPollMsg] = useState('');

  // editor state
  const [editedScript, setEditedScript] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [audioReloadKey, setAudioReloadKey] = useState(0);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // vodcast modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedAvatarId, setSelectedAvatarId] = useState('');
  const [bgType, setBgType] = useState<'color' | 'image' | 'video'>('color');
  const [bgValue, setBgValue] = useState('#0052FF');
  const [dims, setDims] = useState<string[]>(['16:9']);
  const [creating, setCreating] = useState(false);

  const pollCount = useRef(0);
  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadData = useCallback(() => {
    if (!id) return;
    Promise.all([getEpisode(id), getJobLogs(id), getVideoJobsByAudioJob(id)])
      .then(([ep, l, v]) => {
        setEpisode(ep);
        setLogs(l);
        setVideoJobs(v);
        setEditedScript(ep?.script_text ?? '');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadData();
  }, [id, loadData]);

  useEffect(() => {
    if (showModal) {
      listAvatars(clientId).then((a) => {
        setAvatars(a);
        if (a[0]) setSelectedAvatarId(a[0].id);
      });
    }
  }, [showModal, clientId]);

  // ── editor logic ─────────────────────────────────────────────
  const isScriptDirty = useMemo(() => {
    if (!episode?.script_text) return false;
    return editedScript.trim() !== episode.script_text.trim() && editedScript.trim().length > 0;
  }, [editedScript, episode?.script_text]);

  const handleRegenerate = async () => {
    if (!id || !episode || !isScriptDirty) return;
    if (editedScript.length > SCRIPT_MAX_CHARS) {
      setErrorMsg(`Lo script supera ${SCRIPT_MAX_CHARS} caratteri.`);
      return;
    }
    setErrorMsg(null);
    setRegenerating(true);
    try {
      await regenerateEpisode(id, editedScript);
      const fresh = await getEpisode(id);
      setEpisode(fresh);
      setEditedScript(fresh?.script_text ?? '');
      setAudioReloadKey((k) => k + 1);
      // ricarica anche i log per mostrare il nuovo step regenerate
      getJobLogs(id).then(setLogs);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Errore rigenerazione');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedScript(episode?.script_text ?? '');
    setErrorMsg(null);
  };

  // ── download / copy ─────────────────────────────────────────
  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const handleCopyLink = async () => {
    if (!episode?.audio_url) return;
    try {
      await navigator.clipboard.writeText(episode.audio_url);
      showToast('Link copiato negli appunti');
    } catch {
      showToast('Impossibile copiare. Copia manualmente dalla barra indirizzi del player.');
    }
  };

  // ── vodcast polling (preservato dalla v Sprint C) ───────────
  const startPolling = () => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    pollCount.current = 0;
    setPollMsg('');
    pollInterval.current = setInterval(() => {
      pollCount.current += 1;
      if (!id) return;
      getVideoJobsByAudioJob(id).then((v) => {
        setVideoJobs(v);
        const allDone = v.every((j) => j.status === 'ready' || j.status === 'failed');
        if (allDone || pollCount.current >= 30) {
          if (pollInterval.current) clearInterval(pollInterval.current);
          if (!allDone && pollCount.current >= 30) {
            setPollMsg(
              'Alcuni video sono ancora in lavorazione. Ricarica la pagina tra qualche minuto.',
            );
          }
        }
      });
    }, 30000);
  };

  useEffect(() => {
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  const toggleDim = (aspect: string) => {
    setDims((prev) =>
      prev.includes(aspect) ? prev.filter((d) => d !== aspect) : [...prev, aspect],
    );
  };

  const handleCreateVideos = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !selectedAvatarId || dims.length === 0) return;
    setCreating(true);
    try {
      const created = await createVideoJob({
        audio_job_id: id,
        avatar_id: selectedAvatarId,
        background: { type: bgType, value: bgValue },
        dimensions: dims as Array<'16:9' | '9:16' | '1:1'>,
      });
      setVideoJobs((prev) => [...prev, ...created]);
      startPolling();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore nella creazione video');
    } finally {
      setCreating(false);
      setShowModal(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Caricamento…</p>;
  if (!episode) return <p className="text-sm text-slate-500">Episodio non trovato.</p>;

  const audioPlayerUrl = episode.audio_url
    ? `${episode.audio_url}${episode.audio_url.includes('?') ? '&' : '?'}v=${audioReloadKey}`
    : null;
  const charsLeft = SCRIPT_MAX_CHARS - editedScript.length;
  const productionName = episode.production?.name ?? 'Edizione';
  const productionBrand = episode.production?.brand_name ?? '';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{productionName}</h1>
          <p className="mt-1 text-xs text-slate-500">
            {productionBrand && <span>{productionBrand} · </span>}
            Creato il{' '}
            {new Date(episode.created_at).toLocaleString('it-IT', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={episode.status} />
          {episode.regeneration_count > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              Rigenerazioni: {episode.regeneration_count}
            </span>
          )}
          {episode.script_text_edited && (
            <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-800">
              Script modificato
            </span>
          )}
        </div>
      </div>

      {/* ── Player ── */}
      {(episode.status === 'voice_ready' || episode.status === 'ready') && audioPlayerUrl && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-700">Audio</h2>
          <div className="mt-3">
            <AudioPlayer key={audioReloadKey} url={audioPlayerUrl} />
          </div>
        </div>
      )}

      {/* ── Editor script + rigenera ── */}
      {episode.script_text !== null && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Script</h2>
            <span className={`text-xs ${charsLeft < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
              {editedScript.length} / {SCRIPT_MAX_CHARS}
            </span>
          </div>
          <textarea
            value={editedScript}
            onChange={(e) => setEditedScript(e.target.value)}
            disabled={regenerating}
            maxLength={SCRIPT_MAX_CHARS}
            rows={10}
            className="mt-3 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
          />

          {regenerating && (
            <div className="mt-3 flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <svg
                className="h-4 w-4 animate-spin text-brand-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              Sto rigenerando l'audio… circa 25 secondi
            </div>
          )}

          {errorMsg && (
            <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{errorMsg}</p>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={!isScriptDirty || regenerating || editedScript.length > SCRIPT_MAX_CHARS}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {regenerating ? 'Rigenerazione…' : 'Rigenera audio con questo testo'}
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={!isScriptDirty || regenerating}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annulla modifiche
            </button>
          </div>
        </div>
      )}

      {/* ── Download & Condivisione ── */}
      {episode.audio_url && episode.status === 'ready' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-700">Download e condivisione</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href={episode.audio_url}
              download
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Scarica MP3
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Copia link pubblico
            </button>
          </div>
        </div>
      )}

      {/* ── Vodcast (preservato Sprint C) ── */}
      {episode.status === 'ready' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">Vodcast</h2>
            <button
              onClick={() => setShowModal(true)}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              + Genera video
            </button>
          </div>

          {pollMsg && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {pollMsg}
            </p>
          )}

          {videoJobs.length === 0 ? (
            <p className="mt-3 text-xs text-slate-500">
              Nessun video generato per questo radiogiornale.
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              {videoJobs.map((vj) => (
                <div key={vj.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-700">
                      {vj.dimension.aspect} ({vj.dimension.width}x{vj.dimension.height})
                    </span>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${VIDEO_STATUS_CLASS[vj.status]}`}
                    >
                      {VIDEO_STATUS_LABEL[vj.status]}
                    </span>
                  </div>
                  {vj.status === 'ready' && vj.video_url && (
                    <div className="mt-3">
                      <VideoPlayer url={vj.video_url} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Log ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-700">Log</h2>
        {logs.length === 0 ? (
          <p className="mt-2 text-xs text-slate-500">Nessun log disponibile.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {logs.map((log) => (
              <li key={log.id} className="flex items-start gap-3 text-sm">
                <span
                  className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                    log.status === 'ok'
                      ? 'bg-emerald-500'
                      : log.status === 'error'
                        ? 'bg-rose-500'
                        : 'bg-slate-400'
                  }`}
                />
                <div>
                  <p className="font-medium text-slate-800">{log.step}</p>
                  <p className="text-xs text-slate-500">
                    {log.message} · {new Date(log.created_at).toLocaleTimeString('it-IT')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Toast ── */}
      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-50 rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-lg">
          {toastMsg}
        </div>
      )}

      {/* ── Modal Genera Video (invariato) ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900">Genera vodcast</h3>
            <p className="mt-1 text-sm text-slate-500">
              Seleziona avatar, background e dimensioni.
            </p>

            <form onSubmit={handleCreateVideos} className="mt-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700">Avatar</label>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {avatars.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setSelectedAvatarId(a.id)}
                      className={`rounded-lg border p-2 text-left transition ${
                        selectedAvatarId === a.id
                          ? 'border-brand-500 ring-1 ring-brand-500'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <img
                        src={a.preview_url}
                        alt={a.name}
                        className="aspect-video w-full rounded-md object-cover"
                      />
                      <p className="mt-1 truncate text-xs font-medium text-slate-800">{a.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Background</label>
                <div className="mt-2 flex gap-2">
                  {(['color', 'image', 'video'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setBgType(t)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${
                        bgType === t
                          ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                          : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-2">
                  {bgType === 'color' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={bgValue}
                        onChange={(e) => setBgValue(e.target.value)}
                        className="h-10 w-14 rounded border border-slate-200 p-1"
                      />
                      <span className="text-sm text-slate-600">{bgValue}</span>
                    </div>
                  )}
                  {bgType === 'image' && (
                    <input
                      type="url"
                      placeholder="https://example.com/bg.jpg"
                      value={bgValue}
                      onChange={(e) => setBgValue(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                  {bgType === 'video' && (
                    <input
                      type="url"
                      placeholder="https://example.com/bg.mp4"
                      value={bgValue}
                      onChange={(e) => setBgValue(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Dimensioni</label>
                <div className="mt-2 flex flex-wrap gap-3">
                  {[
                    { value: '16:9', label: '16:9 YouTube' },
                    { value: '9:16', label: '9:16 Reels' },
                    { value: '1:1', label: '1:1 Feed' },
                  ].map((d) => (
                    <label
                      key={d.value}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                        dims.includes(d.value)
                          ? 'border-brand-500 bg-brand-50 text-brand-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={dims.includes(d.value)}
                        onChange={() => toggleDim(d.value)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={creating || !selectedAvatarId || dims.length === 0}
                  className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating ? 'Generazione…' : 'Genera'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
